import type { ReactNode } from 'react'
import type { LeadField } from '@/api/types/crm/crm.types'

export type HistoryFieldChange = {
  field_id?: number
  field_name?: string
  name?: string
  old_value?: unknown
  new_value?: unknown
}

export const trimHistoryValue = (v: unknown) =>
  v === null || v === undefined ? '' : String(v).trim()

export function buildFieldNamesById(
  fields: LeadField[] | undefined,
): Map<number, string> {
  const map = new Map<number, string>()
  if (!fields?.length) return map

  for (const field of fields) {
    const name = trimHistoryValue(field.name)
    if (!name) continue
    if (field.field_id != null) map.set(field.field_id, name)
    if (field.id != null) map.set(field.id, name)
  }

  return map
}

export function resolveFieldLabel(
  change: HistoryFieldChange,
  fieldNamesById?: Map<number, string>,
): string {
  const fromPayload =
    trimHistoryValue(change.field_name) || trimHistoryValue(change.name)
  if (fromPayload) return fromPayload

  const id = change.field_id
  if (id != null && fieldNamesById?.has(id)) {
    return fieldNamesById.get(id)!
  }
  if (id != null) return `Поле #${id}`
  return 'Поле'
}

type FormatFieldChangeOpts = {
  mode: 'update' | 'create'
  fieldNamesById?: Map<number, string>
  Em: ({ children }: { children: ReactNode }) => ReactNode
}

export function formatFieldChangeLine(
  change: HistoryFieldChange,
  { mode, fieldNamesById, Em }: FormatFieldChangeOpts,
): ReactNode | null {
  const label = resolveFieldLabel(change, fieldNamesById)
  const oldV = trimHistoryValue(change.old_value)
  const newV = trimHistoryValue(change.new_value)

  if (mode === 'create') {
    if (!newV) return null
    return (
      <>
        <Em>{label}</Em>: <Em>{newV}</Em>
      </>
    )
  }

  if (oldV && newV) {
    return (
      <>
        <Em>{label}</Em>: <Em>{oldV}</Em> → <Em>{newV}</Em>
      </>
    )
  }
  if (!oldV && newV) {
    return (
      <>
        <Em>{label}</Em>: <Em>{newV}</Em>
      </>
    )
  }
  if (oldV && !newV) {
    return (
      <>
        <Em>{label}</Em>: удалено (было «<Em>{oldV}</Em>»)
      </>
    )
  }
  if (!oldV && !newV) {
    return (
      <>
        <Em>{label}</Em>: очищено
      </>
    )
  }
  return (
    <>
      <Em>{label}</Em>: изменено
    </>
  )
}

export function collectFieldChangeLines(
  changes: unknown,
  opts: FormatFieldChangeOpts,
): ReactNode[] {
  if (!Array.isArray(changes)) return []

  return changes
    .map((raw) => formatFieldChangeLine(raw as HistoryFieldChange, opts))
    .filter((line): line is ReactNode => line != null)
}
